import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { X, Sparkle, LinkSimple, PlusCircle, Quotes, Trash } from "@phosphor-icons/react";
import LangInput from "./LangInput";

const KIND_LABEL = {
  project: "Research Project",
  paper: "Research Paper",
  participation: "Call for Participation",
  organization: "University / Organisation",
  topic: "Research Topic",
};

const LINK_KINDS = [
  { v: "article", label: "Article" },
  { v: "news", label: "News" },
  { v: "paper", label: "Paper" },
  { v: "video", label: "Video" },
  { v: "podcast", label: "Podcast" },
  { v: "dataset", label: "Dataset" },
  { v: "government", label: "Gov / Agency" },
  { v: "webpage", label: "Webpage" },
  { v: "other", label: "Other" },
];

/**
 * Shared "researcher submits stuff" form.
 * Covers projects · papers · participation calls · orgs · topics using a
 * `kind` prop. Includes the link-expansion tool so any URL is turned into
 * an editable draft.
 */
export default function ResearchSubmissionModal({ kind, topics = [], defaultTopic = "", onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    submitter_name: "",
    submitter_email: "",
    submitter_affiliation: "",
    topic: defaultTopic || "",
    region: "",
    duration_days: kind === "participation" ? 90 : "",
    observations_needed: "",
    contact_url: "",
  });
  const [links, setLinks] = useState([{ url: "", label: "", kind: "article" }]);
  const [citations, setCitations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const expandFirstLink = async () => {
    const url = (links[0]?.url || "").trim();
    if (!url) { setError("Paste a link first."); return; }
    setExpanding(true); setError("");
    try {
      const { data } = await http.post("/nature/stories/analyze", { url });
      const draft = data?.draft || {};
      if (draft.title && !form.title) set("title", draft.title);
      if (draft.summary && !form.description) set("description", draft.summary);
      if (draft.tags?.length && !form.topic) {
        const match = draft.tags.find((t) => topics.includes(t.toLowerCase()));
        if (match) set("topic", match.toLowerCase());
      }
      setLinks((L) => [{ ...L[0], label: draft.title || L[0].label }, ...L.slice(1)]);
      toast.success("Link expanded — review and edit before saving.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't expand this link. Add details manually.");
    } finally { setExpanding(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim() || !form.submitter_name.trim()) {
      setError("Please fill in Full name, Subject and Description.");
      return;
    }
    setSaving(true); setError("");
    try {
      const payload = {
        kind,
        title: form.title.trim(),
        description: form.description.trim(),
        submitter_name: form.submitter_name.trim(),
        submitter_email: form.submitter_email.trim() || undefined,
        submitter_affiliation: form.submitter_affiliation.trim() || undefined,
        topic: form.topic || undefined,
        region: form.region.trim() || undefined,
        duration_days: form.duration_days ? Number(form.duration_days) : undefined,
        observations_needed: form.observations_needed.trim() || undefined,
        contact_url: form.contact_url.trim() || undefined,
        links: links.filter((L) => (L.url || "").trim()),
        citations: citations.filter((C) => (C.text || "").trim()),
      };
      const { data } = await http.post("/research/submit", payload);
      toast.success("Submitted for review — thank you.");
      onCreated?.(data?.submission);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit. Try again in a moment.");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-0 md:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="research-modal-backdrop"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full md:max-w-2xl bg-[#FBF6EB] md:rounded-3xl overflow-hidden border border-black/10 shadow-2xl my-0 md:my-8"
        data-testid="research-modal"
      >
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 px-5 md:px-8 pt-5 md:pt-6 pb-4 bg-[#FBF6EB]/95 backdrop-blur border-b border-black/5">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              / Research · new {kind}
            </div>
            <h2 className="mt-1 font-serif-display text-2xl md:text-3xl leading-tight tracking-tight">
              Submit a {KIND_LABEL[kind] || "research entry"}
            </h2>
          </div>
          <button
            type="button" onClick={onClose}
            data-testid="research-modal-close"
            className="p-1.5 rounded-full hover:bg-black/5 shrink-0"
            aria-label="Close"
          >
            <X size={20} weight="bold" />
          </button>
        </header>

        <div className="px-5 md:px-8 py-6 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Link expansion — first, so it can pre-fill title/description/topic */}
          <div
            className="rounded-xl border border-dashed border-[color:var(--np-brand,#B85F2A)]/50 bg-[#FBF2E1]/60 p-4"
            data-testid="research-link-expand"
          >
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5 mb-1">
              <Sparkle size={11} weight="fill" /> Have a link? Auto-fill this form
            </div>
            <p className="text-xs text-[#3A3830] mb-2">
              Paste any article, paper, video, dataset or news URL. We'll extract the title and a summary so you can review and edit before saving.
            </p>
            <div className="flex gap-2 flex-wrap">
              <input
                value={links[0]?.url || ""}
                onChange={(e) => setLinks((L) => [{ ...L[0], url: e.target.value }, ...L.slice(1)])}
                placeholder="https://…"
                data-testid="research-link-url"
                className="flex-1 min-w-[220px] bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
              />
              <button
                type="button" onClick={expandFirstLink} disabled={expanding}
                data-testid="research-link-expand-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#B85F2A] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50 hover:opacity-90"
              >
                <Sparkle size={11} weight="fill" />
                {expanding ? "Expanding…" : "Expand"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full name *" testid="research-name">
              <input value={form.submitter_name} onChange={(e) => set("submitter_name", e.target.value)} required maxLength={200} className={fieldCls} />
            </Field>
            <Field label="Email" testid="research-email">
              <input type="email" value={form.submitter_email} onChange={(e) => set("submitter_email", e.target.value)} maxLength={200} className={fieldCls} />
            </Field>
            <Field label="University / Organisation" testid="research-affiliation">
              <input value={form.submitter_affiliation} onChange={(e) => set("submitter_affiliation", e.target.value)} maxLength={300} className={fieldCls} />
            </Field>
            <Field label="Topic" testid="research-topic">
              <select value={form.topic} onChange={(e) => set("topic", e.target.value)} className={fieldCls}>
                <option value="">— pick one —</option>
                {topics.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Subject / Title *" testid="research-title">
            <input value={form.title} onChange={(e) => set("title", e.target.value)} required maxLength={300} className={fieldCls} />
          </Field>

          <Field label="Description *" testid="research-desc">
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} required rows={5} maxLength={8000} className={fieldCls} placeholder="Explain the project, paper or ask. What are you studying? Why does it matter?" />
          </Field>

          {kind === "participation" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Region observers needed" testid="research-region">
                <input value={form.region} onChange={(e) => set("region", e.target.value)} placeholder="e.g. Kerala, India" maxLength={200} className={fieldCls} />
              </Field>
              <Field label="Duration (days)" testid="research-duration">
                <input type="number" min={1} max={1825} value={form.duration_days} onChange={(e) => set("duration_days", e.target.value)} className={fieldCls} />
              </Field>
              <Field label="Contact URL / form" testid="research-contact">
                <input value={form.contact_url} onChange={(e) => set("contact_url", e.target.value)} placeholder="https://…" maxLength={500} className={fieldCls} />
              </Field>
              <div className="md:col-span-3">
                <Field label="What should observers record?" testid="research-obs-needed">
                  <textarea value={form.observations_needed} onChange={(e) => set("observations_needed", e.target.value)} rows={3} maxLength={2000} placeholder="e.g. A photo of any monarch butterfly you see, the plant it was feeding on, and the time of day." className={fieldCls} />
                </Field>
              </div>
            </div>
          )}

          {/* Attribution links */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
                <LinkSimple size={11} weight="fill" /> Attribution — articles, videos, podcasts, datasets
              </div>
              <button type="button" onClick={() => setLinks((L) => [...L, { url: "", label: "", kind: "article" }])} className="text-[color:var(--np-brand,#B85F2A)] text-xs font-mono-tech tracking-[0.24em] uppercase inline-flex items-center gap-1" data-testid="research-add-link">
                <PlusCircle size={12} weight="fill" /> Add link
              </button>
            </div>
            <div className="space-y-2">
              {links.map((L, i) => (
                <div key={i} className="grid grid-cols-[110px_1fr_1fr_28px] gap-2" data-testid={`research-link-row-${i}`}>
                  <select value={L.kind} onChange={(e) => setLinks((cur) => cur.map((x, j) => j === i ? { ...x, kind: e.target.value } : x))} className={fieldCls + " text-xs"}>
                    {LINK_KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
                  </select>
                  <input value={L.url} onChange={(e) => setLinks((cur) => cur.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} placeholder="https://…" className={fieldCls + " text-xs"} />
                  <input value={L.label} onChange={(e) => setLinks((cur) => cur.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Label (optional)" className={fieldCls + " text-xs"} />
                  <button type="button" onClick={() => setLinks((cur) => cur.filter((_, j) => j !== i))} className="text-[#3A3830] hover:text-red-700" aria-label="Remove link"><Trash size={13} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Citations */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
                <Quotes size={11} weight="fill" /> Citations (optional)
              </div>
              <button type="button" onClick={() => setCitations((c) => [...c, { text: "", url: "" }])} className="text-[color:var(--np-brand,#B85F2A)] text-xs font-mono-tech tracking-[0.24em] uppercase inline-flex items-center gap-1" data-testid="research-add-citation">
                <PlusCircle size={12} weight="fill" /> Add citation
              </button>
            </div>
            <div className="space-y-2">
              {citations.map((C, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_28px] gap-2" data-testid={`research-citation-row-${i}`}>
                  <input value={C.text} onChange={(e) => setCitations((cur) => cur.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} placeholder="Author, year, title, journal…" className={fieldCls + " text-xs"} />
                  <input value={C.url || ""} onChange={(e) => setCitations((cur) => cur.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} placeholder="URL / DOI (optional)" className={fieldCls + " text-xs"} />
                  <button type="button" onClick={() => setCitations((cur) => cur.filter((_, j) => j !== i))} className="text-[#3A3830] hover:text-red-700" aria-label="Remove citation"><Trash size={13} /></button>
                </div>
              ))}
            </div>
          </div>

          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="research-modal-error">{error}</div>}
        </div>

        <div className="flex items-center justify-end gap-3 p-5 md:p-6 border-t border-black/10 bg-white/60">
          <button type="button" onClick={onClose} className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]/70 hover:text-[#241209]">Cancel</button>
          <button
            type="submit" disabled={saving}
            data-testid="research-modal-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[#B85F2A] text-white px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase hover:opacity-90 disabled:opacity-60"
          >
            <Sparkle size={13} weight="fill" /> {saving ? "Saving…" : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
}

const fieldCls = "w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]";

function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
